import apiClient from "@/services/api-client";
import type {
  PlanPayload,
  PlanSummary,
  SubscriptionDetail,
  SubscriptionInvoiceItem,
  SubscriptionListQuery,
  SubscriptionListResponse,
  SubscriptionPaymentResponse,
} from "@/types";

function idempotencyKey() {
  return crypto.randomUUID();
}

export const subscriptionService = {
  list: async (params: SubscriptionListQuery) => {
    const response = await apiClient.get<SubscriptionListResponse>(
      "/subscriptions",
      { params },
    );
    return response.data;
  },
  getById: async (id: string) => {
    const response = await apiClient.get<SubscriptionDetail>(
      `/subscriptions/${id}`,
    );
    return response.data;
  },
  listPayments: async (
    id: string,
    params?: { page?: number; limit?: number },
  ) => {
    const response = await apiClient.get<SubscriptionPaymentResponse>(
      `/subscriptions/${id}/payments`,
      { params },
    );
    return response.data;
  },
  listInvoices: async (id: string) => {
    const response = await apiClient.get<SubscriptionInvoiceItem[]>(
      `/subscriptions/${id}/invoices`,
    );
    return response.data;
  },
  updateStatus: async (id: string, status: string, reason: string) => {
    const response = await apiClient.patch<SubscriptionDetail>(
      `/subscriptions/${id}`,
      { status, reason },
      { headers: { "Idempotency-Key": idempotencyKey() } },
    );
    return response.data;
  },
  refundPayment: async (
    paymentId: string,
    payload: { amount?: number; reason?: string },
  ) => {
    const response = await apiClient.post(
      `/subscriptions/payments/${paymentId}/refund`,
      payload,
      { headers: { "Idempotency-Key": idempotencyKey() } },
    );
    return response.data;
  },
  markChargeback: async (
    paymentId: string,
    payload: { reason: string; status?: string },
  ) => {
    const response = await apiClient.post(
      `/subscriptions/payments/${paymentId}/chargeback`,
      payload,
      { headers: { "Idempotency-Key": idempotencyKey() } },
    );
    return response.data;
  },
  listPlans: async (includeInactive = false) => {
    const response = await apiClient.get<PlanSummary[]>("/plans", {
      params: { includeInactive },
    });
    return response.data;
  },
  createPlan: async (payload: PlanPayload) => {
    const response = await apiClient.post<PlanSummary>("/plans", payload, {
      headers: { "Idempotency-Key": idempotencyKey() },
    });
    return response.data;
  },
  updatePlan: async (id: string, payload: Partial<PlanPayload>) => {
    const response = await apiClient.patch<PlanSummary>(
      `/plans/${id}`,
      payload,
      { headers: { "Idempotency-Key": idempotencyKey() } },
    );
    return response.data;
  },
};
